import React, { FC, ReactNode } from 'react';
import './MediaBox.css';
import { DeleteButton } from '../Buttons';
import { IoTrash } from 'react-icons/io5';
import FrameView from '../FrameView/FrameView';
import ImageView from '../ImageView/ImageView';
import { MediaItem } from '@/Shared/Types';
import { LightBoxHook } from '@/Shared/Logic';

interface MediaBoxProps {
	legend: string,
	mediaItems: MediaItem[],
	onDelete?: (itemId: number) => void,
	icon?: ReactNode,
	type: 'image' | 'video',
	lightBoxHook : LightBoxHook
}

/**
 * =====================================================
 * Component: MediaBox
 * =====================================================
 *
 * Description:
 * Generic media gallery component used in domain detail pages.
 * Displays a list of images or videos with optional delete actions
 * and integrates with a LightBox controller for media preview.
 *
 * Responsibilities:
 * - Render media items (images or videos) in a gallery layout
 * - Delegate media preview to a LightBox via an external hook
 * - Expose optional delete actions per media item
 * - Handle empty state rendering
 *
 * Notes:
 * - This component is intentionally stateless regarding media state.
 * - LightBox behavior is fully controlled by the provided hook
 *   (command-style pattern).
 * - Delete actions are optional and controlled by the parent.
 *
 * Author: Samano CASTRE
 * Date: February 4, 2026
 */
const MediaBox: FC<MediaBoxProps> = ({ 
	legend, 
	mediaItems, 
	onDelete, 
	icon, 
	type, 
	lightBoxHook 
}) => {

	/** =====================================================
	 * Derived State
	 * =====================================================
	 *
	 * These flags drive conditional rendering only.
	 * No business logic or side effects are handled here.
	 */
	const noItem = mediaItems.length === 0;
	const isImage = type === 'image';
	const isVideo = type === 'video';

	const byTypeClass = (isImage ? 'image ' : 'video ');
	const byQtyClass = (mediaItems.length >= 3 ? 'multiple' : 'single');


	/** =====================================================
	 * Render
	 * =====================================================
	 *
	 * Renders a gallery of media items:
	 * - Clicking an item opens the LightBox at the correct index
	 * - Delete button is rendered only if onDelete is provided
	 * - Video items render an overlay to indicate interactivity
	 */
	return (
		<fieldset className='MediaBox ' data-testid='MediaBox'>
			<legend>{legend}</legend>
			<div className='Gallery'>
				{ mediaItems.map((item, index) => (

					<div className={"Gallery-item " + byTypeClass + byQtyClass} key={item.id} onClick={() => {
							lightBoxHook.openLightBox({
									medias : mediaItems,
									initialIndex : index
								});
							}
						}>

						{ isImage && (
							<ImageView 
								src={item.small || item.large} 
								alt={item.caption || item.type} 
								/>
							) 
						}

						{ isVideo && (
								<>
									<FrameView url={item.large} />
									<div className='Gallery-item__overlay'></div>
								</>
							)
						}

						<div className='fixed-header-actions flex-row'>
							{ onDelete && (
								<DeleteButton 
									onClick={() => onDelete(item.id as number)} 
									icon={icon || <IoTrash />} 
									/>
								)
							}
						</div>

					</div>
				))}
				{noItem && <div>Aucun média à afficher.</div>}
			</div>
		</fieldset>
	);
};

export default MediaBox;
